<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import type { SelectLineValueType, SelectLine } from '@/objects/SelectLine';
import type { ITranslationkey, IIcon, IIconPosition } from '../Interfaces';
import { DropdownComp } from '..';
import { DropdownItemCheckbox, DropdownItemSeparator, type DropdownItemTypes } from '@/objects';
import type { IDropDownExposed } from './BootstrapWrappers/DropdownComp/IDropDownExposed';
import { ref } from 'vue';

const props = withDefaults(
    defineProps<
        {
            modelValue: Array<SelectLineValueType>;
            data: Array<SelectLine> | Map<SelectLineValueType, SelectLine>;

            /**
             * Wether to show the select all checkbox or not
             */
            hasSelectAllCheckbox?: boolean;

            /**
             * If true we select all the options on load (this will naturally update the modelValue)
             */
            selectAllOptionsOnLoad?: boolean;

            /**
             * Optional text to show when the list is empty
             */
            emptytext?: string;

            /**
             * If we should show the marker for selected items
             * @default true
             */
            showSelectedMarker?: boolean;

            /**
             * A list of additional dropdown items to show in the dropdown, these will be shown after the checkboxes
             */
            dropdownItems?: DropdownItemTypes[];
        } & IIcon &
            IIconPosition &
            ITranslationkey
    >(),
    {
        hasSelectAllCheckbox: false,
        selectAllOptionsOnLoad: false,
        showSelectedMarker: true,
    },
);

/**
 * Internal value
 */
const value = computed<Array<SelectLineValueType>>({
    get: () => props.modelValue,
    set: (v) => emit('update:modelValue', v),
});

/**
 * We want to support both an array and a map as data, but we want to work with an array internally, so we convert it if it's a map. We also want to apply the datasource filter if it's set, but since this component is used in a lot of places we want to keep it simple and not add a filter function, so we assume that if the data is a map, it's already filtered and ready to use. If we want to add a filter function in the future, we can easily do that by adding a computed property that applies the filter to the array.
 */
const dataAsSelectLineArray = computed(() => {
    if (props.data instanceof Map) {
        return Array.from(props.data.values());
    }

    return props.data;
});

/**
 * Emit events
 */
const emit = defineEmits<{
    (e: 'update:modelValue', value: Array<SelectLineValueType>): void;
    (e: 'change', checked: boolean, value: SelectLineValueType, index: number): void;
}>();

/**
 * Helper to check if all the options are checked
 * We only want the "ALL" checkbox to be checked if all the other checkboxes are checked
 */
const isAllChecked = computed({
    get() {
        return dataAsSelectLineArray.value.length == props.modelValue.length;
    },
    set(nv) {
        value.value = nv ? dataAsSelectLineArray.value.map((d) => d.value) : [];
    },
});

const isAnyChecked = computed(() => {
    return dataAsSelectLineArray.value.length > 0 && props.modelValue.length > 0;
});

onMounted(() => {
    /**
     * If the selectall is set, we set the value to all the values to select all
     */
    if (props.selectAllOptionsOnLoad) {
        value.value = dataAsSelectLineArray.value.map((d) => d.value);
    }
});

const dropdownItems = computed(() => [
    // Add selectAll and separator if selectall is true
    ...(props.hasSelectAllCheckbox
        ? [
              DropdownItemCheckbox.create({
                  label: 'general.all',
                  modelValue: isAllChecked,
              }),
              DropdownItemSeparator.create(),
          ]
        : []),

    // Add all the options from the data
    ...dataAsSelectLineArray.value.map((d) => {
        return DropdownItemCheckbox.create({
            label: d.text,
            subtext: d.subtext,
            modelValue: computed({
                get() {
                    return props.modelValue.includes(d.value);
                },
                set(nv) {
                    emit('update:modelValue', nv ? [...props.modelValue, d.value] : props.modelValue.filter((v) => v != d.value));
                },
            }),
            rightDotColor: d.color,
        });
    }),

    // Add additional dropdown items if they exist
    ...(props.dropdownItems ?? []),
]);

const dropdownRef = ref<InstanceType<typeof DropdownComp>>();

defineExpose<IDropDownExposed>({
    show: () => dropdownRef.value?.show(),
    hide: () => dropdownRef.value?.hide(),
    toggle: () => dropdownRef.value?.toggle(),
});
</script>

<template>
    <div class="position-relative input-dropdown">
        <span
            v-if="isAnyChecked && showSelectedMarker"
            class="position-absolute top-0 end-0 overlay-1 triangle pe-none"
        ></span>
        <DropdownComp
            ref="dropdownRef"
            auto-close="outside"
            :translationkey
            :icon
            :icon-position="iconPosition"
            :dropdownItems="dropdownItems"
            :emptytext="emptytext"
        >
            <template v-slot:closedContent>
                <slot />
            </template>
        </DropdownComp>
    </div>
</template>
